import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type {
    Blocks,
    ContentBlock,
    ListGroups,
    MediaSlot as MediaSlotRow,
    MediaSlots,
    SiteFrame
} from '@/types/content'

import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { CtaButton } from '@/components/generals/cta-button'
import { Layout } from '@/components/generals/layout'
import { MediaSlot } from '@/components/generals/media-slot'
import { SectionIntro } from '@/components/generals/section-intro'
import { Seo } from '@/components/generals/seo'
import { FeatureListSection } from '@/components/sections/feature-list-section'
import { PageHero } from '@/components/sections/page-hero'
import { cms, hasValue, isPlaceholder } from '@/lib/cms'
import { reveal } from '@/lib/reveal'
import { REVALIDATE_SECONDS, getPageContent } from '@/lib/content'

interface AboutPageProps {
    frame: SiteFrame
    blocks: Blocks
    groups: ListGroups
    media: MediaSlots
}

interface StoryProps {
    block?: ContentBlock
    portrait?: MediaSlotRow
}

// Personal background: headline on the left, the owner's own story and a second portrait on the right.
const Story: FC<StoryProps> = ({ block, portrait }): ReactNode => {
    if (!block) return null

    return (
        <section id='hintergrund' aria-labelledby='hintergrund-titel' className='relative bg-paper py-24 sm:py-32 lg:py-40'>
            <Container className='grid gap-14 lg:grid-cols-12 lg:gap-10'>
                <div className='lg:col-span-5'>
                    <div className='flex flex-col gap-10 lg:sticky lg:top-[calc(var(--header-height)+2rem)]'>
                        <SectionIntro block={block} headingId='hintergrund-titel' size='lg' showText={false} />

                        <div className='hidden lg:block' {...reveal(160)}>
                            <MediaSlot slot={portrait} slotKey='BROTHERKAJKO_PORTRAIT_02' className='aspect-[3/4] w-full max-w-sm' tone='dark' showCaption captionClassName='text-smoke' />
                        </div>
                    </div>
                </div>

                <div className='flex flex-col gap-10 lg:col-span-6 lg:col-start-7 lg:pt-4'>
                    {hasValue(block.text) && (
                        <p
                            data-mdlfy-cms={cms('inhalte', block.id, 'text')}
                            className={cn('whitespace-pre-line text-xl leading-relaxed text-ink/85 sm:text-[1.375rem]', isPlaceholder(block.text) && 'is-placeholder')}
                            {...reveal(80)}
                        >
                            {block.text}
                        </p>
                    )}

                    <div className='lg:hidden' {...reveal(120)}>
                        <MediaSlot slot={portrait} slotKey='BROTHERKAJKO_PORTRAIT_02' className='aspect-[3/4] w-full' tone='dark' showCaption captionClassName='text-smoke' />
                    </div>
                </div>
            </Container>
        </section>
    )
}

interface QuoteProps {
    block?: ContentBlock
}

// The owner's guiding sentence as a large editorial quote.
const Quote: FC<QuoteProps> = ({ block }): ReactNode => {
    if (!block || !hasValue(block.ueberschrift)) return null

    return (
        <section aria-label='Leitsatz' className='relative overflow-hidden bg-ink py-24 text-bone sm:py-32 lg:py-40'>
            <div aria-hidden='true' className='grain-layer' />

            <Container className='relative'>
                <figure className='mx-auto flex max-w-5xl flex-col gap-8' {...reveal()}>
                    <span aria-hidden='true' className='type-display text-[5rem] leading-[0.6] text-ember-bright sm:text-[7rem]'>
                        „
                    </span>

                    <blockquote>
                        <p
                            data-mdlfy-cms={cms('inhalte', block.id, 'ueberschrift')}
                            className={cn('type-display whitespace-pre-line text-display-lg', isPlaceholder(block.ueberschrift) && 'is-placeholder')}
                        >
                            {block.ueberschrift}
                        </p>
                    </blockquote>
                </figure>
            </Container>
        </section>
    )
}

const AboutPage: FC<AboutPageProps> = ({ frame, blocks, groups, media }): ReactNode => {
    const seo = blocks['ueber.seo']

    return (
        <Layout frame={frame}>
            <Seo
                title={seo?.ueberschrift ?? 'Über mich'}
                description={seo?.text ?? undefined}
                siteUrl={frame.settings.webseite}
                path='/ueber-mich'
            />

            <PageHero
                block={blocks['ueber.hero']}
                media={media.BROTHERKAJKO_PORTRAIT_01}
                slotKey='BROTHERKAJKO_PORTRAIT_01'
                mediaClassName='aspect-[4/5]'
            >
                <CtaButton settings={frame.settings} size='lg' />
            </PageHero>

            <Story block={blocks['ueber.geschichte']} portrait={media.BROTHERKAJKO_PORTRAIT_02} />

            <Quote block={blocks['ueber.zitat']} />

            <FeatureListSection
                id='philosophie'
                block={blocks['ueber.philosophie']}
                items={groups.philosophie ?? []}
                columns={4}
                surface='bone'
            />

            <FeatureListSection
                id='qualifikationen'
                block={blocks['ueber.qualifikationen']}
                items={groups.qualifikationen ?? []}
                variant='rows'
                surface='stone'
            />

            <FeatureListSection
                id='warum'
                block={blocks['ueber.warum']}
                items={groups.warum ?? []}
                columns={3}
                surface='paper'
            />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<AboutPageProps> = async () => {
    const content = await getPageContent({
        blocks: ['ueber.seo', 'ueber.hero', 'ueber.geschichte', 'ueber.zitat', 'ueber.philosophie', 'ueber.qualifikationen', 'ueber.warum'],
        groups: ['philosophie', 'qualifikationen', 'warum'],
        media: ['BROTHERKAJKO_PORTRAIT_01', 'BROTHERKAJKO_PORTRAIT_02']
    })

    return {
        props: content,
        revalidate: REVALIDATE_SECONDS
    }
}

export default AboutPage
